import { useState, useEffect } from "react";
import { Loader2, CheckCircle2, Trash2, ExternalLink, ShieldCheck, Ban, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const LOGOS = {
  mercadopago: "/gateways/mercadopago.png",
  lynx: "/gateways/lynx.png",
  asaas: "/gateways/asaas.png",
};

function GwLogo({ id, className = "h-7" }) {
  if (!LOGOS[id]) return null;
  return <img src={LOGOS[id]} alt={id} className={`${className} w-auto object-contain`} onError={(e) => (e.target.style.display = "none")} />;
}

export default function ResellerGateways() {
  const [data, setData] = useState(null);
  const [mpToken, setMpToken] = useState("");
  const [mpPub, setMpPub] = useState("");
  const [lynxKey, setLynxKey] = useState("");
  const [asaasKey, setAsaasKey] = useState("");
  const [asaasEnv, setAsaasEnv] = useState("production");
  const [saving, setSaving] = useState("");
  const [activating, setActivating] = useState("");
  const { user } = useAuth();

  const load = () => api.get("/reseller/gateways").then(({ data }) => setData(data)).catch(() => setData({ gateways: [], active_gateway: null }));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-muted-foreground">Carregando...</div>;

  const byId = (id) => data.gateways.find((g) => g.id === id);
  const mp = byId("mercadopago"), lynx = byId("lynx"), asaas = byId("asaas");
  const active = data.active_gateway;

  const activate = async (gateway) => {
    setActivating(gateway);
    try {
      await api.post("/reseller/gateways/activate", { gateway });
      toast.success(gateway === "none" ? "Recebimento desativado (loja só com créditos)" : "Gateway ativado");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setActivating(""); }
  };
  const saveMp = async () => {
    if (mpToken.trim().length < 10) return toast.error("Cole um Access Token válido do Mercado Pago");
    setSaving("mercadopago");
    try {
      await api.put("/reseller/gateways/mercadopago", { access_token: mpToken.trim(), public_key: mpPub.trim() || null });
      toast.success("Mercado Pago conectado!"); setMpToken(""); setMpPub(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };
  const saveLynx = async () => {
    if (lynxKey.trim().length < 8) return toast.error("Cole uma API Key válida da Lynx");
    setSaving("lynx");
    try {
      await api.put("/reseller/gateways/lynx", { api_key: lynxKey.trim() });
      toast.success("Lynx conectada!"); setLynxKey(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };
  const saveAsaas = async () => {
    if (asaasKey.trim().length < 8) return toast.error("Cole uma API Key válida do Asaas");
    setSaving("asaas");
    try {
      await api.put("/reseller/gateways/asaas", { api_key: asaasKey.trim(), environment: asaasEnv });
      toast.success("Asaas conectado!"); setAsaasKey(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };
  const removeG = async (id) => {
    setSaving(id);
    try { await api.delete(`/reseller/gateways/${id}`); toast.success("Credenciais removidas"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const ReceiveBtn = ({ id, name, sub, g, testid }) => {
    const isActive = active === id || (id === "none" && !active);
    const ready = id === "none" || (g?.connected && g?.enabled_by_admin);
    return (
      <button data-testid={testid}
        onClick={() => {
          if (id === "none") return activate("none");
          if (ready) activate(id);
          else toast.error(g?.enabled_by_admin ? "Configure suas credenciais primeiro" : `${name} indisponível no momento`);
        }}
        className={`group relative flex flex-col gap-2 rounded-xl border p-4 text-left transition-all ${isActive ? "border-primary ring-2 ring-primary/40" : "border-border hover:bg-muted/60"} ${!ready && id !== "none" ? "opacity-60" : ""}`}>
        <div className="flex h-8 items-center">
          {id === "none" ? <span className="flex items-center gap-2 font-medium"><Ban className="h-5 w-5 text-primary" /> Nenhum</span> : <GwLogo id={id} />}
        </div>
        <p className="text-xs text-muted-foreground">{sub}</p>
        {isActive && <Badge className="w-fit border-green-500/30 bg-green-500/15 text-green-600 dark:text-green-400" variant="outline">Em uso</Badge>}
      </button>
    );
  };

  const GatewayCard = ({ id, g, connected, help, children }) => (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={`${id}-config`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-16 items-center justify-center rounded-lg border border-border bg-white/95 p-1.5"><GwLogo id={id} className="h-6" /></span>
          <h3 className="font-heading font-semibold">{g?.name}</h3>
        </div>
        {connected
          ? <Badge className="gap-1 border-green-500/30 bg-green-500/15 text-green-600 dark:text-green-400" variant="outline" data-testid={`${id}-connected`}><CheckCircle2 className="h-3 w-3" /> Conectado</Badge>
          : <Badge variant="secondary" data-testid={`${id}-disconnected`}>Não conectado</Badge>}
      </div>
      {!g?.enabled_by_admin
        ? <p className="mt-3 rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground" data-testid={`${id}-unavailable`}>Indisponível no momento. Fale com o administrador para habilitar.</p>
        : children}
    </div>
  );

  return (
    <div className="max-w-2xl space-y-6" data-testid="reseller-gateways">
      <div>
        <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Recebimentos</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          O dinheiro (R$) das vendas da sua loja cai <b>direto na sua conta</b> do gateway escolhido. A VULPIX só debita os
          créditos da ativação. Sem gateway ativo, sua loja aceita <b>apenas pagamento com créditos</b>.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card p-5" data-testid="receive-selector">
        <h3 className="font-heading font-semibold">Onde você recebe</h3>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <ReceiveBtn id="mercadopago" name="Mercado Pago" sub="PIX na sua conta MP." g={mp} testid="receive-mercadopago" />
          <ReceiveBtn id="lynx" name="Lynx" sub="PIX na sua conta Lynx." g={lynx} testid="receive-lynx" />
          <ReceiveBtn id="asaas" name="Asaas" sub="PIX na sua conta Asaas." g={asaas} testid="receive-asaas" />
          <ReceiveBtn id="none" name="Nenhum" sub="Só créditos." testid="receive-none" />
        </div>
        {activating && <Loader2 className="mt-3 h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {/* Mercado Pago */}
      <GatewayCard id="mercadopago" g={mp} connected={mp?.connected}>
        {mp?.connected ? (
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 p-3">
              <div><div className="text-xs uppercase tracking-wide text-muted-foreground">Access Token</div><div className="font-mono text-sm" data-testid="mercadopago-token-masked">{mp.token_masked}</div></div>
              <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => removeG("mercadopago")} disabled={saving === "mercadopago"} data-testid="mp-remove-btn"><Trash2 className="h-4 w-4" /> Remover</Button>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Seu token fica criptografado.</p>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="space-y-2"><Label>Access Token (produção)</Label><Input value={mpToken} onChange={(e) => setMpToken(e.target.value)} placeholder="APP_USR-..." data-testid="mp-token-input" /></div>
            <div className="space-y-2"><Label>Public Key (opcional)</Label><Input value={mpPub} onChange={(e) => setMpPub(e.target.value)} placeholder="APP_USR-..." data-testid="mp-publickey-input" /></div>
            <div className="flex items-center justify-between">
              <a href="https://www.mercadopago.com.br/developers/panel/app" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Onde pegar minhas credenciais</a>
              <Button onClick={saveMp} disabled={saving === "mercadopago"} data-testid="mp-save-btn">{saving === "mercadopago" && <Loader2 className="h-4 w-4 animate-spin" />} Salvar e validar</Button>
            </div>
          </div>
        )}
      </GatewayCard>

      {/* Lynx */}
      <GatewayCard id="lynx" g={lynx} connected={lynx?.connected}>
        {lynx?.connected ? (
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 p-3">
              <div><div className="text-xs uppercase tracking-wide text-muted-foreground">API Key</div><div className="font-mono text-sm" data-testid="lynx-token-masked">{lynx.token_masked}</div></div>
              <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => removeG("lynx")} disabled={saving === "lynx"} data-testid="lynx-remove-btn"><Trash2 className="h-4 w-4" /> Remover</Button>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Sua chave fica criptografada.</p>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="space-y-2"><Label>API Key (Lynx)</Label><Input value={lynxKey} onChange={(e) => setLynxKey(e.target.value)} placeholder="lynx_live_..." data-testid="lynx-key-input" /></div>
            <div className="flex items-center justify-between">
              <a href="https://lynxpix.cc" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Onde pegar minha API Key</a>
              <Button onClick={saveLynx} disabled={saving === "lynx"} data-testid="lynx-save-btn">{saving === "lynx" && <Loader2 className="h-4 w-4 animate-spin" />} Salvar</Button>
            </div>
          </div>
        )}
      </GatewayCard>

      {/* Asaas */}
      <GatewayCard id="asaas" g={asaas} connected={asaas?.connected}>
        {asaas?.connected ? (
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 p-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">API Key · {asaas.environment === "sandbox" ? "Sandbox" : "Produção"}</div>
                <div className="font-mono text-sm" data-testid="asaas-token-masked">{asaas.token_masked}</div>
              </div>
              <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => removeG("asaas")} disabled={saving === "asaas"} data-testid="asaas-remove-btn"><Trash2 className="h-4 w-4" /> Remover</Button>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Sua chave fica criptografada. O cliente informará o CPF ao pagar.</p>
            <div className="rounded-md border border-dashed border-border p-3">
              <p className="text-xs font-medium">URL do webhook (cole no painel Asaas → Integrações → Webhooks) para baixa instantânea</p>
              <div className="mt-1.5 flex items-center gap-2">
                <Input readOnly value={`${API}/webhooks/asaas/${user?.id || ""}`} className="font-mono text-[11px]" data-testid="asaas-webhook-url" />
                <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(`${API}/webhooks/asaas/${user?.id || ""}`); toast.success("URL copiada!"); }} data-testid="asaas-webhook-copy"><Copy className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="space-y-2">
              <Label>Ambiente</Label>
              <div className="inline-flex overflow-hidden rounded-md border border-border">
                <button onClick={() => setAsaasEnv("production")} data-testid="asaas-env-production" className={`px-3 py-1.5 text-sm ${asaasEnv === "production" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>Produção</button>
                <button onClick={() => setAsaasEnv("sandbox")} data-testid="asaas-env-sandbox" className={`px-3 py-1.5 text-sm ${asaasEnv === "sandbox" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>Sandbox</button>
              </div>
            </div>
            <div className="space-y-2"><Label>API Key (Asaas)</Label><Input value={asaasKey} onChange={(e) => setAsaasKey(e.target.value)} placeholder="$aact_prod_..." data-testid="asaas-key-input" /></div>
            <div className="flex items-center justify-between">
              <a href="https://docs.asaas.com/docs/authentication" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Onde pegar minha API Key</a>
              <Button onClick={saveAsaas} disabled={saving === "asaas"} data-testid="asaas-save-btn">{saving === "asaas" && <Loader2 className="h-4 w-4 animate-spin" />} Salvar e validar</Button>
            </div>
          </div>
        )}
      </GatewayCard>
    </div>
  );
}
